import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, User, Car, MapPin, Calendar, Hash, Shield, ClipboardCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const RECORD_TYPE_LABELS = {
  car: 'Light Vehicle',
  '3w': '3-Wheeler',
  bus: 'Bus',
  truck: 'Truck',
  trailer: 'Trailer',
  cng_storage: 'CNG Storage',
};

const STATUS_COLORS = {
  valid: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  expired: 'bg-amber-100 text-amber-700 border-amber-300',
  pending: 'bg-blue-100 text-blue-700 border-blue-300',
  failed: 'bg-red-100 text-red-700 border-red-300',
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return d.slice(0, 10);
  }
};

/**
 * Inline duplicate-detected alert.
 *
 * Props:
 *  - field: 'registration_number' | 'storage_serial_number' | 'cylinder_serial'
 *  - value: the duplicate value entered by the user
 *  - existingRecord: enriched record payload from the validation endpoint
 *  - sameFacility: optional flag — true when the duplicate is in the same record being edited
 *  - inFormLocation: optional string — e.g. "Cylinder #2" (for in-form duplicates)
 *  - onDismiss?: optional callback to clear the warning
 */
export default function DuplicateAlert({ field, value, existingRecord, sameFacility = false, inFormLocation = null, onDismiss }) {
  // In-form duplicate (no DB record yet) — show a compact alert
  if (inFormLocation && !existingRecord) {
    const fieldLabel = field === 'cylinder_serial' ? 'cylinder serial number' : 'identifier';
    return (
      <div
        className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3 shadow-sm animate-in fade-in slide-in-from-top-2"
        data-testid="duplicate-alert"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h4 className="font-semibold text-amber-900 text-sm">Duplicate in this facility</h4>
              {onDismiss && (
                <Button variant="ghost" size="sm" onClick={onDismiss} className="h-6 px-2 text-amber-700 hover:bg-amber-100" data-testid="duplicate-alert-dismiss">
                  Dismiss
                </Button>
              )}
            </div>
            <p className="text-sm text-amber-800 mt-1">
              The {fieldLabel}{' '}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-200 text-amber-900 rounded font-mono font-bold text-sm border border-amber-400">
                <Hash className="h-3 w-3" />
                {value}
              </span>{' '}
              is already entered as <strong>{inFormLocation}</strong> in this facility. Please use a unique serial.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!existingRecord) return null;

  const fieldLabel = {
    registration_number: 'Registration Number',
    storage_serial_number: 'Storage Serial Number',
    cylinder_serial: 'Cylinder Serial Number',
  }[field] || 'Identifier';

  const recordLocation = existingRecord.record_type === 'cng_storage'
    ? `Storage Record (${existingRecord.storage_serial_number || existingRecord.id?.slice(0, 8)})`
    : `${RECORD_TYPE_LABELS[existingRecord.record_type] || 'Vehicle'} Record (${existingRecord.registration_number || existingRecord.id?.slice(0, 8)})`;

  const locationDesc = sameFacility
    ? `this same facility — ${recordLocation}`
    : recordLocation;

  const latest = existingRecord.latest_inspection;
  const status = latest?.status || 'draft';

  return (
    <div
      className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4 shadow-sm animate-in fade-in slide-in-from-top-2"
      data-testid="duplicate-alert"
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h4 className="font-semibold text-amber-900">{sameFacility ? 'Duplicate in This Facility' : 'Duplicate Record Detected'}</h4>
            {onDismiss && (
              <Button variant="ghost" size="sm" onClick={onDismiss} className="h-6 px-2 text-amber-700 hover:bg-amber-100" data-testid="duplicate-alert-dismiss">
                Dismiss
              </Button>
            )}
          </div>
          <p className="text-sm text-amber-800 mt-1">
            The {fieldLabel.toLowerCase()}{' '}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-200 text-amber-900 rounded font-mono font-bold text-sm border border-amber-400">
              <Hash className="h-3 w-3" />
              {value}
            </span>{' '}
            already exists in <strong>{locationDesc}</strong>.
          </p>
        </div>
      </div>

      {/* Existing record details */}
      <div className="mt-3 ml-8 p-3 bg-white rounded-md border border-amber-200 space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Existing Record</p>
          {latest?.status && (
            <Badge variant="outline" className={STATUS_COLORS[status] || ''}>
              {status.toUpperCase()}
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          {existingRecord.owner_name && (
            <div className="flex items-center gap-2">
              <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">Owner:</span>
              <span className="font-medium truncate">{existingRecord.owner_name}</span>
            </div>
          )}
          {(existingRecord.make || existingRecord.model) && (
            <div className="flex items-center gap-2">
              <Car className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">Vehicle:</span>
              <span className="font-medium truncate">
                {[existingRecord.make, existingRecord.model, existingRecord.year].filter(Boolean).join(' ')}
              </span>
            </div>
          )}
          {existingRecord.region && (
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">Region:</span>
              <span className="font-medium truncate">{existingRecord.region}</span>
            </div>
          )}
          {existingRecord.created_by_name && (
            <div className="flex items-center gap-2">
              <Shield className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">Created by:</span>
              <span className="font-medium truncate">{existingRecord.created_by_name}</span>
            </div>
          )}
          {existingRecord.created_at && (
            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">Created:</span>
              <span className="font-medium">{formatDate(existingRecord.created_at)}</span>
            </div>
          )}
          {typeof existingRecord.inspection_count === 'number' && (
            <div className="flex items-center gap-2">
              <ClipboardCheck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">Inspections:</span>
              <span className="font-medium">{existingRecord.inspection_count}</span>
            </div>
          )}
        </div>

        {/* Latest inspection summary */}
        {latest && (
          <div className="mt-2 pt-2 border-t border-amber-100">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Latest Inspection</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {latest.verification_number && (
                <div className="truncate">
                  <span className="text-muted-foreground">Verification:</span>{' '}
                  <span className="font-mono text-xs font-medium">{latest.verification_number}</span>
                </div>
              )}
              {latest.certificate_number && (
                <div className="truncate">
                  <span className="text-muted-foreground">Certificate:</span>{' '}
                  <span className="font-mono text-xs font-medium">{latest.certificate_number}</span>
                </div>
              )}
              {latest.inspector_name && (
                <div className="truncate">
                  <span className="text-muted-foreground">Inspector:</span>{' '}
                  <span className="font-medium">{latest.inspector_name}</span>
                </div>
              )}
              {latest.valid_to && (
                <div className="truncate">
                  <span className="text-muted-foreground">Valid to:</span>{' '}
                  <span className="font-medium">{formatDate(latest.valid_to)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="mt-3 ml-8 flex flex-col sm:flex-row gap-2">
        <Button asChild size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="open-existing-record">
          <Link to={`/records/${existingRecord.id}`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4 mr-1.5" />
            Open Existing Record
          </Link>
        </Button>
        <p className="text-xs text-amber-800 self-center">
          Please use a different {fieldLabel.toLowerCase()} or recertify the existing record instead.
        </p>
      </div>
    </div>
  );
}
